/**
 * Dropdown component — Events and Methods
 *
 * Depends on: utility.js (Utility, BREAKPOINTS)
 * Must be loaded synchronously after utility.js and before global.js.
 *
 * HTML contract (do not change without updating JS):
 *   [is="drop-down"]          — root dropdown element
 *   .dropdown-toggle          — the trigger button inside the root
 *   .slideout-submenu         — optional back/close button inside the dropdown
 *   .dropdown.open            — open state class applied to the root
 *   .section-header           — Shopify section wrapper; receives `.header-action`
 *                               while any descendant `.dropdown.megamenu.open`,
 *                               `.predictive-search.show-results`, or
 *                               `custom-drawer#header-search-layer.open` exists
 *
 * @param {HTMLElement} element - Dropdown root element
 */
class Dropdown {
  static MQ_IPAD_PRO_LANDSCAPE = window.matchMedia(`(min-width: ${BREAKPOINTS.ipadProMin}px) and (max-width: ${BREAKPOINTS.ipadProMax}px)`);
  static SECTION_HEADER_SELECTOR = '.section-header';
  static OPEN_MEGAMENU_SELECTOR = '.dropdown.megamenu.open';
  static OPEN_PREDICTIVE_SEARCH_SELECTOR = '.predictive-search.show-results';
  static OPEN_SEARCH_DRAWER_SELECTOR = 'custom-drawer#header-search-layer.open';
  static HEADER_ACTION_CLASS = 'header-action';

  /**
   * Sync the `header-action` class on the closest `.section-header` ancestor
   * based on whether any megamenu, predictive search results, OR the search
   * drawer (opened via `.open-searchform` / `[data-toggleDrawer="header-search-layer"]`)
   * inside it is active.
   * @param {HTMLElement} element - Any element inside the header
   */
  static syncHeaderActionState(element) {
    if (!element) return;
    const sectionHeader = element.closest(Dropdown.SECTION_HEADER_SELECTOR);
    if (!sectionHeader) return;

    const hasOpenMegamenu = sectionHeader.querySelector(Dropdown.OPEN_MEGAMENU_SELECTOR);
    const hasPredictiveResults = sectionHeader.querySelector(Dropdown.OPEN_PREDICTIVE_SEARCH_SELECTOR);
    const hasOpenSearchDrawer = sectionHeader.querySelector(Dropdown.OPEN_SEARCH_DRAWER_SELECTOR);

    if (hasOpenMegamenu || hasPredictiveResults || hasOpenSearchDrawer) {
      Utility.CSS.add(sectionHeader, Dropdown.HEADER_ACTION_CLASS);
    } else {
      Utility.CSS.remove(sectionHeader, Dropdown.HEADER_ACTION_CLASS);
    }
  }

  static collapse(dropdown) {
    if (!dropdown) return;

    Utility.CSS.remove(dropdown, 'open');

    const toggle = dropdown.querySelector('.dropdown-toggle');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');

    dropdown.querySelectorAll('.dropdown.open').forEach(child => {
      Utility.CSS.remove(child, 'open');
      const childToggle = child.querySelector('.dropdown-toggle');
      if (childToggle) childToggle.setAttribute('aria-expanded', 'false');
    });

    Dropdown.syncHeaderActionState(dropdown);
  }

  constructor(element) {
    this.dropdown = element;
    this.toggleCTA = (Utility?.DomSelector?.$('.dropdown-toggle', element)) || element.querySelector('.dropdown-toggle');
    this.supportedEvents = ["click", "touchstart", "touchmove", "touchend", "mouseenter", "mouseleave", "focusout"];
    this.touchState = { startX: 0, startY: 0, moved: false };

    if (!this.toggleCTA) {
      console.warn('Dropdown toggle element not found:', element);
      return;
    }

    this.init();
  }

  init() {
    const handlers = {
      click: (evt) => {
        const redirectTarget = evt.target.closest('[data-target-redirect]');
        if (redirectTarget) {
          evt.stopPropagation();
          window.location.href = redirectTarget.dataset.targetRedirect;
          return;
        }
        this.toggleDropdown();
      },
      touchstart: (evt) => {
        const redirectTarget = evt.target.closest('[data-target-redirect]');
        if (redirectTarget) {
          evt.preventDefault();
          window.location.href = redirectTarget.dataset.targetRedirect;
          return;
        }
        const touch = evt.touches?.[0];
        if (!touch) return;
        this.touchState.startX = touch.clientX;
        this.touchState.startY = touch.clientY;
        this.touchState.moved = false;
      },
      touchmove: (evt) => {
        if (evt.target.closest('[data-target-redirect]')) return;
        const touch = evt.touches?.[0];
        if (!touch || this.touchState.moved) return;

        const deltaX = Math.abs(touch.clientX - this.touchState.startX);
        const deltaY = Math.abs(touch.clientY - this.touchState.startY);
        if (deltaX > 10 || deltaY > 10) {
          this.touchState.moved = true;
        }
      },
      touchend: (evt) => {
        const redirectTarget = evt.target.closest('[data-target-redirect]');
        if (redirectTarget) {
          evt.preventDefault();
          evt.stopPropagation();
          window.location.href = redirectTarget.dataset.targetRedirect;
          return;
        }

        if (this.touchState.moved) {
          this.touchState.moved = false;
          return;
        }
        evt.preventDefault();
        this.toggleDropdown();
        this.touchState.moved = false;
      },
      mouseenter: (evt) => {
        if (evt.target.closest('[data-target-redirect]')) return;
        this.dropdownShow(this.dropdown);
        this.childOnFocusChange(this.dropdown);
      },
      mouseleave: () => this.dropdownHide(this.dropdown),
      focusout: (evt) => {
        if (!this.dropdown.contains(evt.relatedTarget) && this.dropdown.classList.contains('open')) {
          this.dropdownHide(this.dropdown);
        }
      }
    };

    this.supportedEvents.forEach((type) => {
      const target = (type === 'mouseleave' || type === 'focusout') ? this.dropdown : this.toggleCTA;
      const handler = handlers[type];
      if (handler) target.addEventListener(type, handler, { passive: type === 'touchmove' });
    });

    this.bindEvents();
  }

  bindEvents(){
    const closeBtn = Utility.DomSelector.$('.slideout-submenu', this.dropdown);
    if (closeBtn) {
      closeBtn.addEventListener('click', (evt) => {
        evt.preventDefault();
        this.dropdownToggle(this.dropdown);
      });
    }
  }

  /**
   * Toggle dropdown
   * @returns {Dropdown} Returns this for method chaining
   */
  toggleDropdown() {
    this.dropdownToggle(this.dropdown);
    return this;
  }

  /**
   * Toggle dropdown parent
   * @param {HTMLElement} dropdownParent
   * @returns {Dropdown} Returns this for method chaining
   */
  dropdownToggle(dropdownParent) {
    setTimeout(() => {
      dropdownParent.classList.contains('open')
        ? this.dropdownHide(dropdownParent)
        : this.dropdownShow(dropdownParent);
    }, 50);
    return this;
  }

  /**
   * Show dropdown
   * @param {HTMLElement} dropdownParent
   * @returns {Dropdown} Returns this for method chaining
   */
  dropdownShow(dropdownParent) {
    if (!dropdownParent) {
      return this;
    }

    if (Dropdown.MQ_IPAD_PRO_LANDSCAPE.matches && dropdownParent.parentElement) {
      dropdownParent.parentElement
        .querySelectorAll('[is="drop-down"].open')
        .forEach((openDropdown) => {
          if (openDropdown !== dropdownParent) Dropdown.collapse(openDropdown);
        });
    }

    this.toggleCTA.setAttribute('aria-expanded', true);
    Utility.CSS.add(dropdownParent, 'open');
    Dropdown.syncHeaderActionState(dropdownParent);
    return this;
  }

  /**
   * Hide dropdown
   * @param {HTMLElement} dropdownParent
   * @returns {Dropdown} Returns this for method chaining
   */
  dropdownHide(dropdownParent) {
    if (!dropdownParent) return this;

    Dropdown.collapse(dropdownParent);

    return this;
  }

  childOnFocusChange(dropdownParent) {
    const openDropdowns = Array.from(dropdownParent.parentElement.querySelectorAll('.dropdown.open'));
    const currentIndex = openDropdowns.indexOf(dropdownParent);
    openDropdowns.forEach((ele) => {
      const eleIndex = openDropdowns.indexOf(ele);
      if (eleIndex !== currentIndex) {
        Dropdown.collapse(ele);
      }
    });
    return this;
  }
}

// Initialise all dropdown elements on the page
const allDropdowns = Utility.DomSelector.$$('[is="drop-down"]');
allDropdowns.forEach((ele) => {
  new Dropdown(ele);
});

//===============================================================================================================
//          Global Handler for Dropdowns and Custom Selects - Close all open on outside click                  //
//===============================================================================================================

(() => {
  /**
   * Closes all open custom-select dropdowns
   */
  const closeAllOpenSelects = () => {
    document.querySelectorAll('custom-select.open').forEach(select => {
      if (select.closeDropdown && typeof select.closeDropdown === 'function') {
        select.closeDropdown();
      }
    });
  };

  /**
   * Closes all open dropdown elements
   */
  const closeAllOpenDropdowns = () => {
    document.querySelectorAll('[is="drop-down"].open').forEach(dropdown => {
      Dropdown.collapse(dropdown);
    });
  };

  /**
   * Closes all open dropdown/select components
   */
  const closeAllOpenComponents = () => {
    closeAllOpenSelects();
    closeAllOpenDropdowns();
  };

  /**
   * Handles clicks/touches outside dropdown and custom-select elements
   * @param {Event} event - Click or touch event
   */
  const handleOutsideInteraction = (event) => {
    // Don't close if interaction is inside any dropdown or custom-select element
    if (event.target.closest('custom-select') || event.target.closest('[is="drop-down"]')) return;
    closeAllOpenComponents();
  };

  // Add global listeners for both click and touchstart (mobile support)
  document.body.addEventListener('click', handleOutsideInteraction);
  document.body.addEventListener('touchstart', handleOutsideInteraction, { passive: true });
})();

//===============================================================================================================
//          End of Global Handler for Dropdowns and Custom Selects                                              //
//===============================================================================================================
